import React, { FC } from 'react';
import { Plural, t, Trans } from '@lingui/macro';

import { Box, Flex, Heading, Text } from '@rover/kibble/core';
import CalloutBadge from '@rover/react-lib/src/components/callouts/CalloutBadge/CalloutBadge';
import { PriceTransparencyLevel } from '@rover/types';
import { PriceTransparencyLevels } from '@rover/types/src/PriceTransparencyLevel';
import { formatCurrency } from '@rover/utilities/currency';

export type Props = {
  currencyCode: string;
  priceTransparencyLevel?: PriceTransparencyLevel;
  language: string;
  price: string;
  priceInCredits?: number | null;
  shouldShowPriceInCredits?: boolean;
  showFromText?: boolean;
  unitAsSentence: string;
  isTrainingDesign?: boolean;
  isBHUser?: boolean;
};

const PriceColumn: FC<Props> = ({
  currencyCode,
  priceTransparencyLevel,
  language,
  price,
  priceInCredits = null,
  shouldShowPriceInCredits = false,
  showFromText = true,
  unitAsSentence,
  isTrainingDesign = false,
  isBHUser = false,
}) => {
  const getCurrencyLabel = (value: string): string =>
    formatCurrency(value, currencyCode, language, {
      includeDecimal: false,
    });

  const showFromTextLabel = isBHUser
    ? showFromText
    : priceTransparencyLevel !== PriceTransparencyLevels.full && !isTrainingDesign;

  const showBrightHorizonsCalloutBadge =
    isBHUser && shouldShowPriceInCredits && priceInCredits === null;

  return (
    <Flex
      flexDirection="column"
      alignItems="flex-end"
      alignSelf="stretch"
      justifyContent="flex-start"
      width="auto"
      maxWidth="100%"
    >
      {showBrightHorizonsCalloutBadge && (
        <Box marginBottom={showFromTextLabel ? '1x' : '0x'}>
          <CalloutBadge
            value={t`Credits don't apply`}
            calloutBadgeStyle={CalloutBadge.Styles.BRIGHT_HORIZONS_CREDITS_DONT_APPLY}
          />
        </Box>
      )}
      <Flex flexDirection="column" alignItems="flex-end">
        {showFromTextLabel && (
          <Text size="100" textColor="tertiary">
            <Trans>from</Trans>
          </Text>
        )}
        <Flex flexDirection="row" alignItems="flex-end">
          <Heading size="400" responsive={false} mb="0x" textColor="success" as="div">
            {shouldShowPriceInCredits && priceInCredits !== null ? (
              <Plural value={priceInCredits} one="# credit" other="# credits" />
            ) : (
              getCurrencyLabel(price)
            )}
          </Heading>
        </Flex>
        <Text size="100" textColor="tertiary" sx={{ whiteSpace: 'nowrap' }}>
          {(!showFromText && isBHUser) ||
          priceTransparencyLevel === PriceTransparencyLevels.full ? (
            <Trans>total {unitAsSentence}</Trans>
          ) : (
            unitAsSentence
          )}
        </Text>
      </Flex>
    </Flex>
  );
};

export default PriceColumn;
